import { Observable, of, zip } from "rxjs";
import { switchMap } from "rxjs/operators";
import _ from "lodash"

import { AuditCampaign, EntityType, ProjectLinkableItemStateResource } from "@skinsoft/types";
import { SyncUpDependency } from "@skinsoft/classes-interfaces-definition";
import { auditCampaignSelectors } from "@skinsoft/selectors";

import { AuditCampaignDataStore } from "@skinsoft/entity";
import { AuditCampaignOfflineDataUpdateCacher, OfflineDataFetcher, OfflineDataUpdateCacher } from "@skinsoft/offline";
import { EntitySyncUpHandler } from "../../entity-sync-up-handler/entity-sync-up-handler";
import { NewItemIdNotFoundError } from "./errors";
import { WemiSyncUpHandler } from "../wemi";

const DEFAULT_LINKABLE_ITEM_REF_TYPE = "physicalMuseumItem"

export class AuditCampaignSyncUpHandler extends EntitySyncUpHandler<AuditCampaign, AuditCampaign> {
    
    public static getInstance() {
        if (!AuditCampaignSyncUpHandler.instance) {
            AuditCampaignSyncUpHandler.instance = new AuditCampaignSyncUpHandler()
        }

        return AuditCampaignSyncUpHandler.instance
    }

    public static install(Vue) {
        AuditCampaignSyncUpHandler.getInstance()
    }

    private static instance: AuditCampaignSyncUpHandler = null as any

    private constructor() {
        super(
            new AuditCampaignOfflineDataUpdateCacher(),
            AuditCampaignDataStore.getInstance(),
            EntityType.AUDIT_CAMPAIGN,
        )
    }

    getType(): EntityType {
        return this.type
    }

    getDependenciesIds(id: string): Observable<SyncUpDependency[]> {
        return super.getDependenciesIds(id).pipe(
            switchMap(superResult => {
                return zip(
                    of(superResult),
                    this.getCachedEntity(id)
                )
            }),
            switchMap(([superResult, cachedCampaign]) => {
                let result: SyncUpDependency[] = []

                const linkableItems: ProjectLinkableItemStateResource[] = (cachedCampaign as any).workflowSchema?.linkableItems
                if(linkableItems?.length) {
                    for(const linkableItem of linkableItems) {
                        // @ts-ignore
                        const itemId: string = linkableItem.linkableItem?.linkableItem ?? linkableItem.linkableItem 
                        if(itemId && OfflineDataUpdateCacher.isTemporaryId(itemId)) {
                            result.push({
                                id: itemId,
                                type: EntityType.WEMI
                            })
                        }
                    }
                }

                result = superResult.concat(result)

                return of(result)
            })
        )
    }
    
    getAndFormatModificationsBeforeMerge(localEntity: AuditCampaign): Observable<Partial<AuditCampaign>> {
        const allItems = auditCampaignSelectors.linkableItems(localEntity)
                .filter(linkableItem => {
                    return linkableItem.linkableItem?.linkableItem
                })

            const newItemsIds: string[] = allItems?.map(
                item => {
                    const ref = (item.linkableItem?.linkableItem as any).ref ?? item.linkableItem?.linkableItem
                    return ref
                }
            ).filter(
                itemId => {
                    return OfflineDataFetcher.isTemporaryId(itemId)
                }
            )
        
        let formattedCampaign: Partial<AuditCampaign> = {} as any

        if(localEntity) {
            if(localEntity.auditProjectSchema) {
                const auditProjectSchema = localEntity.auditProjectSchema

                if(auditProjectSchema.auditReports) {
                    if(auditProjectSchema.auditReports.length) {
                        for(let i=0; i<auditProjectSchema.auditReports.length; i++) {
                            const auditReport = auditProjectSchema.auditReports[i]

                            // recolement ref
                            if(auditReport) {
                                let ref
                                if((auditReport as any).ref) {
                                    ref = (auditReport as any).ref
                                } else {
                                    ref = auditReport
                                }
                                // the offline created recolements will be created automatically when we push 
                                // the new item, and we will update them later from RecordDataStore (soon to be renamed RecolementDataStore)
                                if(!OfflineDataFetcher.isTemporaryId(ref)) {
                                    _.set(
                                        formattedCampaign,
                                        `auditProjectSchema.auditReports[${i}]`,
                                        ref
                                    )
                                }
                            }
                        }
                    }
                }
                // else nothing, because we cannot suppress recolements from audit campaign in Audit (at least not in offline mode)
            }

            if(localEntity.workflowSchema) {
                const workflowSchema = localEntity.workflowSchema

                if(workflowSchema.linkableItems) {
                    if(workflowSchema.linkableItems.length) {
                        for(let i=0; i<workflowSchema.linkableItems.length; i++) {
                            const linkableItem = workflowSchema.linkableItems[i]

                            // item ref
                            if(linkableItem?.linkableItem) {
                                let ref
                                if((linkableItem.linkableItem as any).ref) {
                                    ref = (linkableItem.linkableItem as any).ref 
                                } 
                                else if(linkableItem.linkableItem.linkableItem) {
                                    ref = linkableItem.linkableItem.linkableItem
                                } 
                                else if(linkableItem.linkableItem instanceof String || typeof linkableItem.linkableItem === "string") {
                                    ref = linkableItem.linkableItem
                                }

                                // in this.pushCachedUpdate and this.applyAfterPushOperations, we keep track of
                                // newly offline created item, and we add them later
                                if(ref) {
                                    if(OfflineDataFetcher.isTemporaryId(ref)) {
                                        const newItemId = WemiSyncUpHandler.getInstance().getNewId(ref)
                                        if(newItemId) {
                                            ref = newItemId
                                        } else {
                                            throw new NewItemIdNotFoundError()
                                        }
                                    }

                                    _.set(
                                        formattedCampaign,
                                        `workflowSchema.linkableItems[${i}].linkableItem.linkableItem`,
                                        ref
                                    )

                                } else {
                                    // we don't need to treat other linkableItem related fields for this index
                                    continue
                                }

                                let refType
                                if((linkableItem.linkableItem as any).referenceType) {
                                    refType = (linkableItem.linkableItem as any).referenceType
                                } else if(linkableItem.linkableItem.linkableItemReferenceType) {
                                    refType = linkableItem.linkableItem.linkableItemReferenceType
                                }

                                if(!refType) {
                                    refType = DEFAULT_LINKABLE_ITEM_REF_TYPE
                                }

                                _.set(
                                    formattedCampaign,
                                    `workflowSchema.linkableItems[${i}].linkableItem.linkableItemReferenceType`,
                                    refType
                                )
                            }
                        }
                    }
                    // else nothing, because we cannot suppress items from audit campaign in Audit (at least not in offline mode)
                }
            }
        }

        return of(formattedCampaign)
    }
}